import { 
    collection, 
    query, 
    where, 
    getDocs, 
    getDocsFromCache,
    doc, 
    updateDoc, 
    serverTimestamp,
    addDoc,
    Timestamp,
    orderBy,
    limit
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Réplica cliente de la lógica de validación de QR de Cloud Functions.
 * Esta función permite validar códigos QR cuando el dispositivo está offline
 * utilizando la persistencia local de Firestore.
 */
export const validateQrOffline = async ({ fraccionamientoId, codigo, auth, userData, checkOnly = false }) => {
    try {
        if (!auth || !auth.currentUser) {
            return { success: false, code: 'unauthenticated', message: 'Debes iniciar sesión para validar códigos.' };
        }

        if (!fraccionamientoId || !codigo) {
            return { success: false, code: 'invalid-argument', message: 'Falta información necesaria para la validación.' };
        }
        const uid = auth.currentUser.uid;
        const guardName = userData?.nombre || userData?.nombreCompleto || 'Personal de vigilancia';
        const role = userData?.rol || 'guardia';

        // 1. Buscar la invitación usando una query "amigable con el caché"
        // En lugar de una query específica por código (que suele fallar offline si no se corrió antes),
        // buscamos las recientes (que ya están en caché por el listener de Vigilancia.js)
        // y filtramos localmente en memoria.
        const invitationsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'invitacionesAcceso');
        // Usamos la misma ordenación y límite que el sync listener de Vigilancia.js para aprovechar el caché
        const qCache = query(invitationsRef, orderBy('creadoEn', 'desc'), limit(150));
        
        let snapshot;
        try {
            console.log('🔍 [OfflineValidator] Buscando invitación en caché de historial...');
            snapshot = await getDocsFromCache(qCache);
        } catch (e) {
            console.log('⚠️ [OfflineValidator] Fallback a getDocs normal en caché:', e.message);
            snapshot = await getDocs(qCache);
        }

        // Buscamos el documento con el código exacto en el set de resultados cacheados
        const cleanCode = String(codigo || '').trim();
        const invDoc = snapshot.docs.find(docSnap => {
            const data = docSnap.data();
            return data.codigo === cleanCode || data.tokenAcceso === cleanCode || data.token === cleanCode;
        });

        if (!invDoc) {
            // Intento final: Probar la query directa por si acaso ya estuviera indexada
            console.log('🔎 [OfflineValidator] No se encontró en set reciente, intentando query directa por código...');
            let directSnap;
            try {
                const qDirect = query(invitationsRef, where('codigo', '==', cleanCode));
                directSnap = await getDocsFromCache(qDirect);
                if (directSnap.empty) {
                    const qToken = query(invitationsRef, where('tokenAcceso', '==', cleanCode));
                    directSnap = await getDocsFromCache(qToken);
                }
            } catch (e) {
                const qDirect = query(invitationsRef, where('codigo', '==', cleanCode));
                directSnap = await getDocs(qDirect);
            }

            if (!directSnap || directSnap.empty) {
                return { success: false, code: 'not-found', message: 'No encontramos una invitación asociada a este código localmente.' };
            }
            return processInvitation(directSnap.docs[0], uid, guardName, checkOnly, fraccionamientoId);
        }

        return processInvitation(invDoc, uid, guardName, checkOnly, fraccionamientoId);
    } catch (error) {
        console.error("OfflineValidator Error:", error);
        return { success: false, code: 'internal', message: 'Error en la validación local: ' + error.message };
    }
};

/**
 * Función auxiliar para procesar los datos de una invitación encontrada.
 */
const processInvitation = (invDoc, uid, guardName, checkOnly, fraccionamientoId) => {
    const invData = invDoc.data();
    const invId = invDoc.id;

    // 2. Validar Estado
    if (invData.estado !== 'pendiente') {
        return { success: false, code: 'invalid-status', message: `Este código ya está en estado: ${invData.estado}` };
    }

    // 3. Validar Fecha (GMT-6)
    const ahora = new Date();
    const timeZoneMX = 'America/Mexico_City';
    const hoyMexico = ahora.toLocaleDateString('en-CA', { timeZone: timeZoneMX });
    
    let fechaVisitaMX = '';
    if (invData.fechaVisita) {
        const dateObj = invData.fechaVisita instanceof Timestamp ? invData.fechaVisita.toDate() : new Date(invData.fechaVisita);
        fechaVisitaMX = dateObj.toLocaleDateString('en-CA', { timeZone: timeZoneMX });
    }

    if (fechaVisitaMX && hoyMexico !== fechaVisitaMX) {
        if (hoyMexico < fechaVisitaMX) {
            return { success: false, code: 'expired', message: `El código aún no es válido. Fecha programada: ${fechaVisitaMX}` };
        } else {
            return { success: false, code: 'expired', message: 'El código QR ya expiró.' };
        }
    }

    // --- MODO SOLO LECTURA ---
    if (checkOnly) {
        return {
            success: true,
            code: 'ok',
            message: 'Código verificado (Pendiente de validación final)',
            visit: { 
                ...invData, 
                id: invId,
                // Normalización para vista previa en modal si es necesario
                visitante: invData.visitanteNombre || invData.visitante || invData.nombre || 'Invitado QR',
                departamento: invData.departamento || invData.numeroCasa || '---'
            }
        };
    }

    // 4. Preparar Actualización y Mapeo para Tabla de Visitantes
    const validationTimestamp = Timestamp.now();
    const isoNow = new Date().toISOString();
    
    const updatePayload = {
        estado: 'validado',
        validadoPorUid: uid,
        validadoPorNombre: guardName,
        validadoEn: validationTimestamp,
        validadoOffline: true,
        actualizadoEn: serverTimestamp(),
    };

    // Objeto normalizado que entiende la tabla "Visitantes Actuales" de Vigilancia.js
    const accessMock = {
        ...invData,
        ...updatePayload,
        id: invId,
        // Campos críticos para la UI de Vigilancia.js
        visitante: invData.visitanteNombre || invData.visitante || invData.nombre || 'Invitado QR',
        departamento: invData.departamento || invData.numeroCasa || '---',
        entradaRegistradaIso: isoNow,
        entradaRegistradaPorNombre: guardName,
        origen: 'qr',
        estadoAcceso: 'dentro',
        numeroVisitantes: Number(invData.numeroVisitantes) || 1,
        categoria: invData.categoria || 'visita',
        esQR: true,
        offline: true
    };

    // 5. Ejecutar actualización local (Fire and forget para evitar cuelgues)
    const invitacionRef = doc(db, 'fraccionamientos', fraccionamientoId, 'invitacionesAcceso', invId);
    updateDoc(invitacionRef, updatePayload).catch(e => console.warn('Offline updateDoc deferred:', e));

    // 6. Registrar movimiento localmente (Fire and forget)
    const notifRef = collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones');
    addDoc(notifRef, {
        fraccionamientoId,
        titulo: `Visita validada (Mode Offline) - ${accessMock.visitante}`,
        descripcion: `El código ${invData.codigo} fue validado offline por ${guardName}.`,
        categoria: 'Vigilancia',
        tipoMovimiento: 'visita_validada_offline',
        origen: 'vigilancia',
        fecha: serverTimestamp(),
        actorUid: uid,
        actorNombre: guardName,
        invitacionId: invId,
        offline: true
    }).catch(e => console.warn('Offline addDoc deferred:', e));

    return {
        success: true,
        code: 'ok',
        message: `Visita de ${accessMock.visitante} validada correctamente (Modo Offline).`,
        visit: accessMock
    };
};
